// EmptyState — centered placeholder used when a list/grid has no rows.
// Brand pass (CEO-approved mockup 28/09/2026, spot 5): an empty list shows the
// four-petal flower of the brand kit, breathing softly, with the title in the
// brand serif on cream. A caller that passes the warning sign "⚠️" is showing
// an ERROR, not an empty list — that keeps its sign and the plain title, so a
// failure never looks like a calm "nothing here yet".
import { type ReactNode } from "react";
import { BRAND_ART, BrandMark } from "./BrandArt";

type Props = {
  /** "⚠️" marks an error state; any other icon is replaced by the brand flower. */
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
};

const WARNING = "⚠️";

export function EmptyState({ icon, title, description, action }: Props) {
  const isWarning = icon === WARNING;
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border bg-[#FFFEF5] px-6 py-12 text-center">
      {isWarning ? (
        <div className="text-[48px] leading-none opacity-50">{icon}</div>
      ) : (
        <BrandMark src={BRAND_ART.flower} className="gn-breathe h-14 w-14 text-[var(--gn-gold)]" />
      )}
      {isWarning ? (
        <h3 className="text-base font-semibold text-[var(--color-text-secondary)]">{title}</h3>
      ) : (
        <h3
          className="text-[20px] font-normal leading-snug text-[var(--color-text)]"
          style={{ fontFamily: "var(--font-art)" }}
        >
          {title}
        </h3>
      )}
      {description && (
        <p className="text-xs text-muted-foreground">{description}</p>
      )}
      {action}
    </div>
  );
}
